// Thin fetch wrapper — no react-query/axios (see CLAUDE.md: 소수의 호출만 필요).
const apiUrl = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// FastAPI error bodies are either {detail: string} (HTTPException) or
// {detail: [{msg, ...}]} (pydantic validation, 422) — normalize both to one message.
function extractMessage(data: unknown, status: number): string {
  if (data && typeof data === "object" && "detail" in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const msgs = detail.map((d) => (d as { msg?: string }).msg).filter(Boolean);
      if (msgs.length) return msgs.join(", ");
    }
  }
  return `요청을 처리하지 못했어요. (${status})`;
}

async function request<T>(method: string, path: string, body?: unknown, token?: string): Promise<T> {
  // FormData 는 Content-Type 을 직접 세우면 안 돼요 — multipart boundary 를 브라우저가 붙여요.
  const isForm = body instanceof FormData;
  const headers: Record<string, string> = {};
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${apiUrl}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, extractMessage(data, res.status));
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, token?: string) => request<T>("GET", path, undefined, token),
  post: <T>(path: string, body: unknown, token?: string) => request<T>("POST", path, body, token),
  put: <T>(path: string, body: unknown, token?: string) => request<T>("PUT", path, body, token),
  patch: <T>(path: string, body: unknown, token?: string) => request<T>("PATCH", path, body, token),
  del: <T>(path: string, token?: string) => request<T>("DELETE", path, undefined, token),
};
